import React, { useState, useEffect, useRef } from "react";
import ReactApexChart from "react-apexcharts";
import ChartErrorBoundary from "./ChartErrorBoundary";
import { withErrorSuppression } from "utils/errorSuppressionUtils";
import { Spinner } from "components";

const CustomerClosedChartInternal = ({ labels, series }) => {
  const [retryKey, setRetryKey] = useState(0);
  const [fadeIn, setFadeIn] = useState(false);
  const [chartReady, setChartReady] = useState(false);
  const initialRenderRef = useRef(true);
  const chartRef = useRef(null);

  const processData = () => {
    if (
      !Array.isArray(series) ||
      series.length === 0 ||
      !Array.isArray(labels) ||
      labels.length === 0
    ) {
      return {
        processedSeries: [{ name: "Total Closed Landlords", data: [0] }],
        processedLabels: ["No Data"],
      };
    }

    const validSeries = series.filter(
      (val) => typeof val === "number" && !isNaN(val)
    );

    if (validSeries.length === 0) {
      return {
        processedSeries: [{ name: "Total Closed Landlords", data: [0] }],
        processedLabels: ["No Data"],
      };
    }

    const minLength = Math.min(validSeries.length, labels.length);
    const finalSeries = validSeries.slice(0, minLength);
    const finalLabels = labels.slice(0, minLength);

    const processedSeries = [
      {
        name: "Total Closed Landlords",
        data: finalSeries,
      },
    ];

    return {
      processedSeries,
      processedLabels: finalLabels,
    };
  };

  useEffect(() => {
    if (initialRenderRef.current) {
      const timer = setTimeout(() => {
        setChartReady(true);
        initialRenderRef.current = false;

        setTimeout(() => {
          setFadeIn(true);
        }, 100);
      }, 500);

      return () => clearTimeout(timer);
    } else {
      setFadeIn(false);
      setChartReady(false);

      const timer = setTimeout(() => {
        setChartReady(true);

        setTimeout(() => {
          setFadeIn(true);
        }, 50);
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [series, labels]);

  useEffect(() => {
    const originalOnError = window.onerror;

    window.onerror = function (message, source, lineno, colno, error) {
      if (
        (source && source.includes("apexcharts")) ||
        (message && message.includes("apexcharts")) ||
        (error && error.stack && error.stack.includes("apexcharts"))
      ) {
        return true;
      }

      if (originalOnError) {
        return originalOnError(message, source, lineno, colno, error);
      }

      return false;
    };

    return () => {
      window.onerror = originalOnError;
    };
  }, []);

  const handleRetry = () => {
    setRetryKey((prev) => prev + 1);
    setFadeIn(false);
    setChartReady(false);

    setTimeout(() => {
      setChartReady(true);
      setTimeout(() => {
        setFadeIn(true);
      }, 50);
    }, 100);
  };

  if (!chartReady) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-96 transition-opacity duration-300 ease-in-out">
        <div className="animate-pulse">
          <Spinner size="large" color="#19a8a6" />
        </div>
      </div>
    );
  }

  const { processedSeries, processedLabels } = processData();

  const chartOptions = {
    chart: {
      type: "bar",
      toolbar: { show: false },
      animations: { enabled: false },
      fontFamily: "inherit",
      background: "transparent",
      sparkline: { enabled: false },
    },
    plotOptions: {
      bar: {
        horizontal: false,
        columnWidth: "50%",
        endingShape: "rounded",
      },
    },
    dataLabels: { enabled: false },
    yaxis: {
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: true,
        borderColor: "#737373",
      },
      axisTicks: {
        show: true,
        borderColor: "#737373",
      },
    },
    xaxis: {
      categories: processedLabels,
      labels: {
        style: {
          colors: "#737373",
          fontSize: "12px",
        },
      },
      axisBorder: {
        show: true,
        borderColor: "#737373",
      },
      axisTicks: {
        show: true,
        borderColor: "#737373",
      },
    },
    tooltip: {
      enabled: true,
      y: {
        formatter: function (value) {
          return value !== undefined ? `${value}` : "0";
        },
      },
    },
    colors: ["#fab54e"],
    grid: { show: false },
  };

  return (
    <ChartErrorBoundary resetKey={retryKey} onRetry={handleRetry}>
      <div
        className={`w-full h-full flex-1 transition-opacity duration-300 ease-in-out ${
          fadeIn ? "opacity-100" : "opacity-0"
        }`}
      >
        {chartReady && (
          <ReactApexChart
            options={chartOptions}
            series={processedSeries}
            type="bar"
            width="100%"
            height="100%"
            ref={chartRef}
            key={`status-chart-${retryKey}-${Math.random()}`}
          />
        )}
      </div>
    </ChartErrorBoundary>
  );
};

// Wrap the component with error suppression HOC
const CustomerClosedChart = withErrorSuppression(CustomerClosedChartInternal);

export default React.memo(CustomerClosedChart);
